/**
 * User Activity Tracker for Session Management
 *
 * Monitors user interactions to determine if token refresh should occur.
 * Follows AWS Cognito security best practices for activity-based authentication.
 */

// Activity thresholds in milliseconds
export const ACTIVITY_THRESHOLDS = {
  RECENT_ACTIVITY_WINDOW: 5 * 60 * 1000, // 5 minutes
  IDLE_THRESHOLD: 30 * 60 * 1000, // 30 minutes
  ABSOLUTE_SESSION_LIMIT: 8 * 60 * 60 * 1000, // 8 hours
  REFRESH_BUFFER_TIME: 5 * 60 * 1000, // 5 minutes before token expiry
} as const;

// Session storage keys
const STORAGE_KEYS = {
  LAST_ACTIVITY: 'last_activity_timestamp',
  SESSION_START: 'session_start_timestamp',
} as const;

export interface ActivityTracker {
  trackActivity(): void;
  getLastActivity(): number;
  getSessionStart(): number;
  isRecentlyActive(threshold?: number): boolean;
  isWithinIdleThreshold(): boolean;
  shouldAllowTokenRefresh(tokenExpiry: number): boolean;
  getSessionDuration(): number;
  cleanup(): void;
}

class ActivityTrackerImpl implements ActivityTracker {
  private lastActivityDebounce: number | null = null;
  private readonly DEBOUNCE_DELAY = 1000; // 1 second debounce
  private eventListeners: Array<() => void> = [];

  constructor() {
    this.initializeSession();
    this.setupEventListeners();
  }

  private initializeSession(): void {
    const now = Date.now();

    // Initialize session start if not exists
    if (!sessionStorage.getItem(STORAGE_KEYS.SESSION_START)) {
      sessionStorage.setItem(STORAGE_KEYS.SESSION_START, now.toString());
    }

    // Initialize last activity
    if (!sessionStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY)) {
      sessionStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());
    }
  }

  private setupEventListeners(): void {
    // Activity events to monitor
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      const listener = () => this.debouncedTrackActivity();
      document.addEventListener(event, listener, { passive: true });
      this.eventListeners.push(() => document.removeEventListener(event, listener));
    });

    // Track page visibility changes
    const visibilityListener = () => {
      if (!document.hidden) {
        this.trackActivity();
      }
    };
    document.addEventListener('visibilitychange', visibilityListener);
    this.eventListeners.push(() =>
      document.removeEventListener('visibilitychange', visibilityListener)
    );
  }

  private debouncedTrackActivity(): void {
    if (this.lastActivityDebounce) {
      clearTimeout(this.lastActivityDebounce);
    }

    this.lastActivityDebounce = window.setTimeout(() => {
      this.trackActivity();
      this.lastActivityDebounce = null;
    }, this.DEBOUNCE_DELAY);
  }

  trackActivity(): void {
    const now = Date.now();
    sessionStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now.toString());
  }

  getLastActivity(): number {
    const timestamp = sessionStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    return timestamp ? parseInt(timestamp, 10) : Date.now();
  }

  getSessionStart(): number {
    const timestamp = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
    return timestamp ? parseInt(timestamp, 10) : Date.now();
  }

  isRecentlyActive(threshold: number = ACTIVITY_THRESHOLDS.RECENT_ACTIVITY_WINDOW): boolean {
    const now = Date.now();
    const lastActivity = this.getLastActivity();
    const timeSinceActivity = now - lastActivity;

    return timeSinceActivity <= threshold;
  }

  isWithinIdleThreshold(): boolean {
    const now = Date.now();
    const lastActivity = this.getLastActivity();
    const timeSinceActivity = now - lastActivity;

    return timeSinceActivity <= ACTIVITY_THRESHOLDS.IDLE_THRESHOLD;
  }

  getSessionDuration(): number {
    const now = Date.now();
    const sessionStart = this.getSessionStart();
    return now - sessionStart;
  }

  shouldAllowTokenRefresh(tokenExpiry: number): boolean {
    const now = Date.now();
    const timeUntilExpiry = tokenExpiry - now;
    const timeSinceActivity = now - this.getLastActivity();
    const sessionDuration = this.getSessionDuration();

    // Don't refresh if user has been idle too long
    if (timeSinceActivity > ACTIVITY_THRESHOLDS.IDLE_THRESHOLD) {
      return false;
    }

    // Don't refresh if absolute session limit reached
    if (sessionDuration > ACTIVITY_THRESHOLDS.ABSOLUTE_SESSION_LIMIT) {
      return false;
    }

    // Refresh if token expires soon and user is recently active
    return (
      timeUntilExpiry <= ACTIVITY_THRESHOLDS.REFRESH_BUFFER_TIME &&
      timeSinceActivity <= ACTIVITY_THRESHOLDS.RECENT_ACTIVITY_WINDOW
    );
  }

  cleanup(): void {
    // Clear debounce timer
    if (this.lastActivityDebounce) {
      clearTimeout(this.lastActivityDebounce);
      this.lastActivityDebounce = null;
    }

    // Remove all event listeners
    this.eventListeners.forEach(removeListener => removeListener());
    this.eventListeners = [];
  }
}

// Singleton instance
let activityTrackerInstance: ActivityTracker | null = null;

export const getActivityTracker = (): ActivityTracker => {
  activityTrackerInstance ??= new ActivityTrackerImpl();
  return activityTrackerInstance;
};

export const cleanupActivityTracker = (): void => {
  if (activityTrackerInstance) {
    activityTrackerInstance.cleanup();
    activityTrackerInstance = null;
  }
};

// Utility functions for session management
export const clearSessionData = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
  sessionStorage.removeItem(STORAGE_KEYS.SESSION_START);
};

export const getTimeUntilIdle = (): number => {
  const tracker = getActivityTracker();
  const now = Date.now();
  const lastActivity = tracker.getLastActivity();
  const timeSinceActivity = now - lastActivity;
  const timeUntilIdle = ACTIVITY_THRESHOLDS.IDLE_THRESHOLD - timeSinceActivity;

  return Math.max(0, timeUntilIdle);
};

export const getTimeUntilAbsoluteTimeout = (): number => {
  const tracker = getActivityTracker();
  const sessionDuration = tracker.getSessionDuration();
  const timeUntilTimeout = ACTIVITY_THRESHOLDS.ABSOLUTE_SESSION_LIMIT - sessionDuration;

  return Math.max(0, timeUntilTimeout);
};
